/**
 * Top-level (pre-auth) route matching for the SPA entry point.
 *
 * These routes are matched against `window.location.pathname` in `App.tsx`
 * BEFORE the auth providers mount, because they must work for logged-out users
 * arriving from an email link (verify-email, accept-invitation, reset-password)
 * or a marketing link (demo).
 *
 * Critical: the matcher tolerates trailing slashes. AWS Amplify / CloudFront
 * hosting 301-redirects extensionless paths to add a trailing slash
 * (e.g. `/verify-email?token=X` → `/verify-email/?token=X`). A naive
 * `pathname === "/verify-email"` check fails for the redirected form and falls
 * through to the default app view, so the page never renders and (for
 * verify-email) the token is never submitted — silently breaking email
 * verification. Normalizing the trailing slash here makes every link form
 * resolve to the same route. See `.kiro/steering/failure-patterns.md` Pattern A
 * (one concept, two representations).
 */
export type TopLevelRoute =
  | "demo"
  | "accept-invitation"
  | "verify-email"
  | "reset-password"
  | "account-delete"
  | "account-delete-confirm"
  | "subscribed"
  | "privacy"
  | "terms"
  | "app";

/** The free-trial signup, for anything that used to send a visitor to the demo. */
export const SIGNUP_PATH = "/?view=signup";

/**
 * Resolve a `window.location.pathname` to a top-level route, ignoring any
 * trailing slash(es). The query string lives in `window.location.search` and is
 * intentionally not consulted here.
 */
export function matchTopLevelRoute(pathname: string): TopLevelRoute {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (path === "/demo" || path.startsWith("/demo/")) return "demo";
  if (path === "/accept-invitation") return "accept-invitation";
  if (path === "/verify-email") return "verify-email";
  if (path === "/reset-password") return "reset-password";
  // /account/delete — public path Google Play requires (account_deletion_handoff).
  // The page is auth-less; entry is an email challenge that emails a single-use
  // link back to /account/delete/confirm?token=… (which is the second variant).
  if (path === "/account/delete") return "account-delete";
  if (path === "/account/delete/confirm") return "account-delete-confirm";
  // /subscribed — synthetic landing for Google Ads conversion tracking.
  // BillingChrome.handleSubscribed hard-navigates here after a paid subscribe
  // so the conversion fires on a real URL hit. See SubscribedPage.tsx.
  if (path === "/subscribed") return "subscribed";
  // /privacy and /terms — public legal deep-links that open the Legal
  // Center directly to the matching doc. No explicit CloudFront
  // CacheBehavior, so they fall through the default behavior to the SPA
  // origin (same as the routes above). See legal/LegalPage.tsx.
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  // /share/<id> is owned by the Astro app (`web/`) and served by CloudFront
  // from the worldbyme-astro-ssr Lambda — see web/src/pages/share/[id].astro.
  // The SPA never sees these requests after the DNS cutover. shareProjectIdFromPath
  // is still exported below because AnonymousProjectViewer (mounted by Astro
  // as a React island) reads the id from window.location.pathname there.
  // /journal/* is likewise served by the Astro app (web/src/pages/journal/*).
  // The SPA falls through to "app" for /journal/*, same model as /share/*.
  return "app";
}

/** Extract the project id from a `/share/<id>` path. Returns empty
 *  string for malformed input — the viewer renders the fallback in
 *  that case. */
export function shareProjectIdFromPath(pathname: string): string {
  const path = pathname.replace(/\/+$/, "");
  if (!path.startsWith("/share/")) return "";
  return path.slice("/share/".length);
}
